import Button from "@mui/material/Button";
import { VpnKey } from "@mui/icons-material";


import {
  ArrayInput,
  Datagrid,
  DeleteButton,
  Edit,
  EditButton,
  email,
  ReferenceInput,
  ReferenceManyField,
  regex,
  required,
  SaveButton,
  SelectInput,
  SimpleForm,
  SimpleFormIterator,
  TextField,
  TextInput,
  Toolbar,
  useNotify,
  useRecordContext
} from "react-admin";
import { AddressInputs } from "../../components/address-inputs.component";
import { Col, Row } from "../../components/grid.component";
import { Text } from "../../components/typography.component";
import { API } from "../../providers/api.provider";
import { formatCPForCNPJ, formatPhone, normalizeDocumentForApi } from "../../utils/mask.util";
import { regexs } from "../../utils/regexs.util";

const Title = ({ record }: any) => <Text gutterBottom={false}>{record?.username || 'Usuário'}</Text>;

const generatePassword = (length: number = 8): string => {
  const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%&*";
  let password = "";
  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * charset.length);
    password += charset[randomIndex];
  }
  return password;
};

const ChangePasswordButton = () => {
  const record = useRecordContext();
  const notify = useNotify();

  const handleChangePassword = async () => {
    if (!record?.id) {
      notify("Usuário não encontrado", { type: "error" });
      return;
    }

    const suggestedPassword = generatePassword(8);
    const newPassword = prompt("Digite a nova senha:", suggestedPassword);
    
    if (newPassword) {
      try {
        await API.patch(`/auth/users/${record.id}/change-password`, {
          newPassword: newPassword
        });
        notify("Senha alterada com sucesso!", { type: "success" });
      } catch (error: any) {
        const errorMessage = error?.response?.data?.message || error?.message || "Erro ao alterar senha";
        notify(errorMessage, { type: "error" });
      }
    }
  };

  return (
    <Button
      variant="outlined"
      color="primary"
      onClick={handleChangePassword}
      startIcon={<VpnKey />}
      style={{ marginTop: 16, marginBottom: 16 }}
    >
      Gerar nova senha
    </Button>
  );
};


const UserEditToolbar = () => {
  const notify = useNotify();

  return (
    <Toolbar sx={{ justifyContent: "space-between" }}>
      <SaveButton />
      <DeleteButton
        mutationMode="pessimistic"
        confirmTitle="Excluir usuário"
        confirmContent="Tem certeza que deseja excluir este usuário? Esta ação não poderá ser desfeita."
        mutationOptions={{
          onError: (error: any) => {
            const raw = error?.body?.message ?? error?.message ?? "Erro ao excluir usuário";
            const message = Array.isArray(raw) ? raw.join(", ") : String(raw);
            notify(message, { type: "error" });
          },
        }}
      />
    </Toolbar>
  );
};

export const UserEdit = (props: any) => {
  return (
    <Edit
      title={<Title />}
      transform={(data: any) => ({
        ...data,
        registration: normalizeDocumentForApi(data.registration),
      })}
      {...props}
    >
      <SimpleForm toolbar={<UserEditToolbar />}>
        <Text variant="subtitle1" display="block">
          Dados básicos
        </Text>
        <Row spacing={4}>
          <Col sm={3}>
            <TextInput fullWidth source="name" label="Nome" validate={required()} />
          </Col>
          <Col sm={3}>
            <TextInput fullWidth source="email" label="E-mail" validate={email()} />
          </Col>
          <Col sm={3}>
            <TextInput fullWidth source="username" label="Usuário" validate={required()} />
          </Col>
          <Col sm={2}>
            <TextInput
              fullWidth
              source="registration"
              defaultValue=""
              parse={(value: string) => value?.toUpperCase()}
              format={(value: string) => formatCPForCNPJ(value)}
              validate={[required(), regex(regexs.CPJ_AND_CNPJ_REGEX, "CPF ou CNPJ Inválido")]}
              label="CPF/CNPJ"
            />
          </Col>
        </Row>
        <Row spacing={2}>
          <Col sm={3}>
            <TextInput source="phone" label="Telefone" fullWidth parse={(value: string) => formatPhone(value)} />
          </Col>          
        </Row>
        <Row spacing={2}>
          <Col sm={12}>
            <ChangePasswordButton />
          </Col>
        </Row>
        <Row spacing={1}></Row>
        <Text variant="subtitle1" display="block">
          Emails secundários
        </Text>
        <Row spacing={1}>
          <Col sm={12}>
            <ArrayInput source="emails" label={null} style={{ marginTop: 0, marginBottom: 16 }}>
              <SimpleFormIterator disableReordering>
                <TextInput source="email" label="Email" validate={email()} />
              </SimpleFormIterator>
            </ArrayInput>
          </Col>
        </Row>
        <Text variant="subtitle1" display="block">
          Telefones
        </Text>
        <Row spacing={1}>
          <Col sm={12}>
            <ArrayInput source="phones" label={null} style={{ marginTop: 0, marginBottom: 16 }}>
              <SimpleFormIterator disableReordering>
                <TextInput source="phone" label="Telefone" parse={(value: string) => formatPhone(value)} />
              </SimpleFormIterator>
            </ArrayInput>
          </Col>
        </Row>
        <AddressInputs />
        <Row spacing={1}>
          <Col sm={12}>
            <ReferenceInput label="Empresa" reference="companies" source="company.id" perPage={1000}>
              <SelectInput optionText="name" fullWidth />
            </ReferenceInput>
          </Col>
        </Row>
        <Row spacing={1}>
          <ReferenceManyField label="Licença" reference="licenses" target="userId">
            <Col sm={12}>
              <Datagrid>
                <TextField source="product.name" label="Produto" />
                <TextField source="serialNumber" label="Serial" />
                <TextField source="version" label="Versão" />
                <EditButton />
              </Datagrid>
            </Col>
          </ReferenceManyField>
        </Row>
        <Row spacing={1}>
          <Col sm={12}>
            <TextInput 
              label="Observações" 
              source="observations" 
              fullWidth 
              multiline
              rows={4}
            />
          </Col>
        </Row>
      </SimpleForm>
    </Edit>
  );
};
